import { Box, Button, type ButtonProps, Typography } from "@mui/material";
import { styled, type Theme } from "@mui/material/styles";
import type { ElementType } from "react";

import { DESKTOP_MIN_WIDTH } from "~/feature/getUpdates/constants";

const onMobile = (theme: Theme) => theme.breakpoints.down(DESKTOP_MIN_WIDTH);

export const Root = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

export const Chip = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  alignSelf: "flex-start",
  gap: 6,
  marginBottom: 18,
  padding: "5px 10px",
  borderRadius: 5,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.5px",
  backgroundColor: "#FDECEC",
  color: "#C62828",

  "& svg": {
    width: 14,
    height: 14,
  },

  [onMobile(theme)]: {
    marginBottom: 14,
  },
}));

export const Headline = styled(Typography)(({ theme }) => ({
  marginBottom: 10,
  fontSize: 28,
  fontWeight: 700,
  lineHeight: 1.18,
  letterSpacing: "-0.6px",

  [onMobile(theme)]: {
    marginBottom: 8,
    fontSize: 22,
  },
}));

export const Highlight = styled("span")({
  padding: "0 6px",
  borderRadius: 5,
  backgroundColor: "#F5B700",
  color: "#0B0C0E",
  boxDecorationBreak: "clone",
});

export const Subhead = styled(Typography)(({ theme }) => ({
  marginBottom: 18,
  fontSize: 14,
  lineHeight: 1.5,
  color: theme.palette.text.secondary,

  [onMobile(theme)]: {
    marginBottom: 14,
    fontSize: 13,
  },
}));

export const PriceRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 14,
  marginBottom: 18,
  padding: "14px 0",
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,

  [onMobile(theme)]: {
    gap: 12,
    marginBottom: 14,
    padding: "12px 0",
  },
}));

export const PriceValue = styled("span")(({ theme }) => ({
  fontSize: 30,
  fontWeight: 800,
  lineHeight: 1,
  letterSpacing: "-0.8px",
  fontVariantNumeric: "tabular-nums",

  [onMobile(theme)]: {
    fontSize: 26,
  },
}));

export const PriceDetails = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 3,
});

export const PriceWorth = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  letterSpacing: "-0.1px",
  color: theme.palette.text.secondary,

  [onMobile(theme)]: {
    fontSize: 12,
  },
}));

export const PriceNote = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: "-0.1px",

  [onMobile(theme)]: {
    fontSize: 12,
  },
}));

export const Eyebrow = styled(Typography)(({ theme }) => ({
  marginBottom: 10,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.7px",
  color: theme.palette.text.secondary,

  [onMobile(theme)]: {
    marginBottom: 8,
  },
}));

export const Bullets = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 12,
  marginBottom: 24,

  [onMobile(theme)]: {
    gap: 10,
    marginBottom: 18,
  },
}));

export const Bullet = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 10,

  [onMobile(theme)]: {
    gap: 8,
  },
}));

export const BulletCheck = styled(Box)(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  color: "#26BD6C",

  "& svg": {
    width: 20,
    height: 20,
  },

  [onMobile(theme)]: {
    "& svg": {
      width: 18,
      height: 18,
    },
  },
}));

export const BulletText = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  lineHeight: 1.42,
  letterSpacing: "-0.1px",

  "& strong": {
    fontWeight: 600,
  },

  [onMobile(theme)]: {
    fontSize: 13,
  },
}));

export const ClaimLink = styled(Button)<
  ButtonProps & { to?: string; component?: ElementType }
>(({ theme }) => {
  const isDark = theme.palette.mode === "dark";

  return {
    marginBottom: 10,
    padding: "14px 24px",
    borderRadius: theme.shape.borderRadius,
    textTransform: "none",
    fontSize: 15,
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: "-0.1px",
    boxShadow: "none",
    backgroundColor: isDark ? theme.palette.common.white : "#111111",
    color: isDark ? "#111111" : theme.palette.common.white,

    "&:hover": {
      boxShadow: "none",
      backgroundColor: isDark ? theme.palette.grey[300] : "#2A2E35",
    },

    "& .MuiButton-endIcon svg": {
      width: 18,
      height: 18,
    },
  };
});

export const Microcopy = styled(Typography)(({ theme }) => ({
  marginBottom: 14,
  fontSize: 12,
  lineHeight: 1.5,
  textAlign: "center",
  color: theme.palette.text.secondary,

  [onMobile(theme)]: {
    marginBottom: 10,
    fontSize: 11,
  },
}));
